import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { HtmlEditorProps } from "../remirror/editor.context";
import { HtmlEditor } from "../remirror/RemirrorEditor";
import {
  type EditorMode,
  getStoredEditorMode,
  validateHtml,
} from "../remirror/editor-func";
import { IconTypes } from "../icons.types";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

export type HtmlEditorDialogProps = {
  isOpen?: boolean;
  onClose?: () => void;
} & HtmlEditorProps;

export type OpenHtmlEditorModalFunc = (params: HtmlEditorProps) => void;

export const HtmlEditorDialog = ({
  value,
  onChange,
  isOpen,
  onClose,
  fields,
  fieldArgs,
  fullScreen: isFullScreen,
  editorId,
}: HtmlEditorDialogProps) => {
  const [html, setHtml] = useState(value);
  const [fullScreen, setFullScreen] = useState(isFullScreen || false);
  const [isPreview, setIsPreview] = useState(false);
  const [htmlWarning, setHtmlWarning] = useState<string | null>(null);
  const [editorMode, setEditorMode] = useState<EditorMode>(getStoredEditorMode);

  // Turn off dialog preview when entering HTML mode (it has its own)
  useEffect(() => {
    if (editorMode === "html") setIsPreview(false);
  }, [editorMode]);

  const productName = fieldArgs?.productName as string | undefined;
  const resolveContent = fieldArgs?.resolveContent as
    | ((html: string) => string)
    | undefined;

  const previewHtml = useMemo(() => {
    if (!isPreview || !resolveContent || !html) return "";
    try {
      return resolveContent(html);
    } catch {
      return html;
    }
  }, [isPreview, resolveContent, html]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <span>Text Editor</span>
            {productName && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontWeight: 400 }}
              >
                — {productName}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {resolveContent && editorMode === "wysiwyg" && (
              <Button
                size="small"
                variant={isPreview ? "contained" : "outlined"}
                color={isPreview ? "primary" : "secondary"}
                startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                onClick={() => setIsPreview(!isPreview)}
                sx={{ textTransform: "none", minWidth: 0 }}
              >
                Preview
              </Button>
            )}
            <Tooltip title={fullScreen ? "Exit full screen" : "Full screen"}>
              <IconButton
                size="small"
                onClick={() => setFullScreen(!fullScreen)}
                aria-label={fullScreen ? "Exit full screen" : "Full screen"}
              >
                {fullScreen ? (
                  <IconTypes.CloseFullscreen />
                ) : (
                  <IconTypes.OpenFullscreen />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <HtmlEditor
              value={html}
              onChange={setHtml}
              fields={fields}
              fieldArgs={fieldArgs}
              fullScreen={fullScreen}
              setFullScreen={setFullScreen}
              editorId={"dialog-" + editorId}
              disableExternalEdit={true}
              localMode={true}
              onModeChange={setEditorMode}
            />
          </Box>
          {isPreview && (
            <Paper
              variant="outlined"
              sx={{
                flex: 1,
                minWidth: 0,
                p: 2,
                overflow: "auto",
                "& img": { maxWidth: "100%" },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1,
                  color: "text.secondary",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  pb: 0.5,
                }}
              >
                Preview (with product data)
              </Typography>
              {html ? (
                <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
              ) : (
                <Typography color="text.secondary" fontStyle="italic">
                  No content to preview
                </Typography>
              )}
            </Paper>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: fullScreen ? "center" : "flex-end",
          padding: "16px 24px",
        }}
      >
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            const warning = validateHtml(html);
            if (warning) {
              setHtmlWarning(warning);
            }
            onChange(html);
            onClose();
          }}
        >
          Save
        </Button>
      </DialogActions>
      <Snackbar
        open={!!htmlWarning}
        autoHideDuration={6000}
        onClose={() => setHtmlWarning(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setHtmlWarning(null)}
          severity="warning"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {htmlWarning}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
